import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ordersService } from '../../services/ordersService';
import { driversService } from '../../services/driversService';
import { ratingsService } from '../../services/ratingsService';
import {
  AdminOrderDetailsResponse,
  OrderStatus,
  OrderStatusChange,
  LatLngPoint,
  OrderMetadata
} from '../../types/orders';
import { DriverLocationResponse } from '../../types/drivers';
import { OrderRatingResponse } from '../../types/ratings';
import AcceptOrderModal from '../modals/AcceptOrderModal';
import CancelOrderModal from '../modals/CancelOrderModal';
import ChangeBranchModal from '../modals/ChangeBranchModal';
import DetachDriverModal from '../modals/DetachDriverModal';
import SendToDriverModal from '../modals/SendToDriverModal';
import SendMessageModal from '../modals/SendMessageModal';
import UpdateDeliveryPriceModal from '../modals/UpdateDeliveryPriceModal';
import ProblematicCompleteModal from '../modals/ProblematicCompleteModal';
import OrderMap from '../map/OrderMap';
import { calculateDistance, formatDistance } from '../../utils/distanceUtils';
import './Page.css';

const OrderDetails: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [orderDetails, setOrderDetails] = useState<AdminOrderDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [driverPath, setDriverPath] = useState<LatLngPoint[]>([]);
  const [nearbyDrivers, setNearbyDrivers] = useState<DriverLocationResponse[]>([]);
  const [metadata, setMetadata] = useState<OrderMetadata[]>([]);
  const [orderRating, setOrderRating] = useState<OrderRatingResponse | null>(null);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [driverPathRefreshing, setDriverPathRefreshing] = useState(false);

  // Modal states
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [changeBranchModalOpen, setChangeBranchModalOpen] = useState(false);
  const [detachDriverModalOpen, setDetachDriverModalOpen] = useState(false);
  const [sendToDriverModalOpen, setSendToDriverModalOpen] = useState(false);
  const [sendMessageModalOpen, setSendMessageModalOpen] = useState(false);
  const [updateDeliveryPriceModalOpen, setUpdateDeliveryPriceModalOpen] = useState(false);
  const [problematicCompleteModalOpen, setProblematicCompleteModalOpen] = useState(false);

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails(parseInt(orderId));
      fetchOrderMetadata(parseInt(orderId));
      fetchOrderRating(parseInt(orderId));
    }
  }, [orderId]);

  useEffect(() => {
    // Fetch driver path when order details change and driver is assigned
    if (orderDetails?.assignedDriver) {
      fetchDriverPath();
    } else {
      setDriverPath([]);
    }
  }, [orderDetails?.assignedDriver?.id]);

  useEffect(() => {
    // Fetch nearby drivers when order is accepted by restaurant but no driver is assigned
    if (orderDetails &&
        !orderDetails.assignedDriver &&
        orderDetails.assignedRestaurantBranch &&
        (orderDetails.orderStatus === OrderStatus.RESTAURANT_ACCEPTED_ORDER ||
         orderDetails.orderStatus === OrderStatus.RESTAURANT_FINISHED_ORDER)) {
      fetchNearbyDrivers();
    } else {
      setNearbyDrivers([]);
    }
  }, [orderDetails?.assignedDriver, orderDetails?.orderStatus, orderDetails?.assignedRestaurantBranch?.id]);

  const fetchOrderDetails = async (id: number) => {
    setLoading(true);
    setError('');

    try {
      const response = await ordersService.getOrderDetails(id);

      if (response.success && response.data) {
        setOrderDetails(response.data);
      } else {
        setError(response.error || t('orderDetails.failedToFetch'));
      }
    } catch (error) {
      setError(t('orderDetails.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverPath = async () => {
    if (!orderDetails) return;

    try {
      const response = await ordersService.getOrderDriverPath(orderDetails.id);
      if (response.success && response.data && Array.isArray(response.data.path)) {
        setDriverPath(response.data.path);
      } else {
        setDriverPath([]);
      }
    } catch (error) {
      console.error('Failed to fetch driver path:', error);
      setDriverPath([]);
    }
  };

  const handleRefreshDriverPath = async () => {
    if (!orderDetails?.assignedDriver) return;
    setDriverPathRefreshing(true);
    await fetchDriverPath();
    setDriverPathRefreshing(false);
  };

  const fetchNearbyDrivers = async () => {
    if (!orderDetails?.assignedRestaurantBranch) return;

    try {
      const response = await driversService.searchDriversOnMap({
        centerLatitude: orderDetails.assignedRestaurantBranch.latitude,
        centerLongitude: orderDetails.assignedRestaurantBranch.longitude,
        radius: 5000, // 5km radius
        limit: 20
      });

      if (response.success && response.data) {
        setNearbyDrivers(response.data);
      } else {
        setNearbyDrivers([]);
      }
    } catch (error) {
      console.error('Failed to fetch nearby drivers:', error);
      setNearbyDrivers([]);
    }
  };

  const fetchOrderMetadata = async (id: number) => {
    try {
      const response = await ordersService.getOrderMetadata(id);

      if (response.success && response.data) {
        setMetadata(response.data);
      } else {
        setMetadata([]);
      }
    } catch (error) {
      console.error('Failed to fetch order metadata:', error);
      setMetadata([]);
    }
  };

  const fetchOrderRating = async (id: number) => {
    setRatingLoading(true);
    try {
      const response = await ratingsService.getOrderRating(id);

      if (response.success && response.data) {
        setOrderRating(response.data);
      } else {
        setOrderRating(null);
      }
    } catch (error) {
      console.error('Failed to fetch order rating:', error);
      setOrderRating(null);
    } finally {
      setRatingLoading(false);
    }
  };

  const formatPrice = (price: number): string => {
    return `${price.toLocaleString()} soum`;
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} style={{ color: i <= rating ? '#FFD700' : '#ddd', fontSize: '1.2rem' }}>
          ★
        </span>
      );
    }
    return <div style={{ display: 'flex', gap: '2px' }}>{stars}</div>;
  };

  const getStatusDisplay = (status: OrderStatus): string => {
    if (orderDetails?.restaurantRejectionReason && status == OrderStatus.ORDER_ACCEPTED_BY_ADMIN) {
        return t('orders.status.restaurantRejected');
    }

    if (orderDetails?.problematicCompleteReason && status == OrderStatus.ORDER_COMPLETED) {
        return t('orders.status.completedWithProblem');
    }

    const statusMap: Record<OrderStatus, string> = {
      [OrderStatus.ORDER_PLACED]: t('orders.status.orderPlaced'),
      [OrderStatus.ORDER_ACCEPTED_BY_ADMIN]: t('orders.status.acceptedByAdmin'),
      [OrderStatus.RESTAURANT_ACCEPTED_ORDER]: t('orders.status.restaurantAccepted'),
      [OrderStatus.RESTAURANT_FINISHED_ORDER]: t('orders.status.readyForPickup'),
      [OrderStatus.DRIVER_PICKED_UP_ORDER]: t('orders.status.inDelivery'),
      [OrderStatus.DRIVER_DROPPED_OFF_ORDER]: t('orders.status.delivered'),
      [OrderStatus.ORDER_COMPLETED]: t('orders.status.completed'),
      [OrderStatus.ADMIN_CANCELLED_ORDER]: t('orders.status.cancelled'),
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: OrderStatus): string => {
    if (orderDetails?.restaurantRejectionReason && status == OrderStatus.ORDER_ACCEPTED_BY_ADMIN) {
        return '#dc3545';
    }

    switch (status) {
      case OrderStatus.ORDER_PLACED:
        return '#000000';
      case OrderStatus.ORDER_ACCEPTED_BY_ADMIN:
        return '#17a2b8';
      case OrderStatus.RESTAURANT_ACCEPTED_ORDER:
        return '#fd7e14';
      case OrderStatus.RESTAURANT_FINISHED_ORDER:
        return '#20c997';
      case OrderStatus.DRIVER_PICKED_UP_ORDER:
        return '#007bff';
      case OrderStatus.DRIVER_DROPPED_OFF_ORDER:
        return '#6f42c1';
      case OrderStatus.ORDER_COMPLETED:
        return '#28a745';
      case OrderStatus.ADMIN_CANCELLED_ORDER:
        return '#dc3545';
      default:
        return '#000000';
    }
  };

  const getStatusChangeDisplay = (change: OrderStatusChange): string => {
    const changeMap: Record<OrderStatusChange, string> = {
      [OrderStatusChange.ORDER_PLACED]: t('orderDetails.statusChanges.orderPlaced'),
      [OrderStatusChange.ADMIN_ACCEPTED_ORDER]: t('orderDetails.statusChanges.adminAcceptedOrder'),
      [OrderStatusChange.ADMIN_ASSIGNED_ORDER_TO_BRANCH]: t('orderDetails.statusChanges.adminAssignedToBranch'),
      [OrderStatusChange.ADMIN_CHANGED_BRANCH]: t('orderDetails.statusChanges.adminChangedBranch'),
      [OrderStatusChange.ADMIN_SENT_ORDER_TO_DRIVER]: t('orderDetails.statusChanges.adminSentToDriver'),
      [OrderStatusChange.ADMIN_DETACHED_ORDER_FROM_DRIVER]: t('orderDetails.statusChanges.adminDetachedFromDriver'),
      [OrderStatusChange.SYSTEM_SENT_ORDER_TO_DRIVERS]: t('orderDetails.statusChanges.systemSentToDrivers'),
      [OrderStatusChange.DRIVER_ACCEPTED_ORDER]: t('orderDetails.statusChanges.driverAcceptedOrder'),
      [OrderStatusChange.RESTAURANT_REJECTED_ORDER]: t('orderDetails.statusChanges.restaurantRejected'),
      [OrderStatusChange.RESTAURANT_ACCEPTED_ORDER]: t('orderDetails.statusChanges.restaurantAccepted'),
      [OrderStatusChange.RESTAURANT_FINISHED_ORDER]: t('orderDetails.statusChanges.restaurantFinished'),
      [OrderStatusChange.DRIVER_PICKED_UP_ORDER]: t('orderDetails.statusChanges.driverPickedUp'),
      [OrderStatusChange.DRIVER_DROPPED_OFF_ORDER]: t('orderDetails.statusChanges.driverDelivered'),
      [OrderStatusChange.ADMIN_CANCELLED_ORDER]: t('orderDetails.statusChanges.adminCancelled'),
      [OrderStatusChange.ORDER_COMPLETED]: t('orderDetails.statusChanges.orderCompleted'),
    };
    return changeMap[change] || change;
  };

  const calculateTotalPrice = (order: AdminOrderDetailsResponse): number => {
    const foodsTotal = order.orderedFoods.reduce((sum, food) => sum + (food.price * food.count), 0);
    return order.deliveryPrice + foodsTotal;
  };

  // Calculate distance between client and restaurant branch
  const getClientToRestaurantDistance = (): string | null => {
    if (!orderDetails?.assignedRestaurantBranch) return null;

    const distance = calculateDistance(
      orderDetails.clientLatitude,
      orderDetails.clientLongitude,
      orderDetails.assignedRestaurantBranch.latitude,
      orderDetails.assignedRestaurantBranch.longitude
    );

    return formatDistance(distance);
  };

  // Calculate distances from client to all available branches (when no branch assigned)
  const getClientToAllBranchesDistances = (): Array<{ branch: any; distance: number; formattedDistance: string }> | null => {
    if (!orderDetails || orderDetails.assignedRestaurantBranch || orderDetails.allRestaurantBranches.length === 0) {
      return null;
    }

    const distances = orderDetails.allRestaurantBranches.map(branch => {
      const distance = calculateDistance(
        orderDetails.clientLatitude,
        orderDetails.clientLongitude,
        branch.latitude,
        branch.longitude
      );

      return {
        branch,
        distance,
        formattedDistance: formatDistance(distance)
      };
    });

    // Sort by distance (closest first)
    return distances.sort((a, b) => a.distance - b.distance);
  };

  // Calculate distance between driver and restaurant (when not picked up)
  const getDriverToRestaurantDistance = (): string | null => {
    if (!orderDetails?.assignedDriver || !orderDetails.assignedRestaurantBranch || driverPath.length === 0) {
      return null;
    }

    // Only show this distance if order hasn't been picked up yet
    if (orderDetails.orderStatus === OrderStatus.DRIVER_PICKED_UP_ORDER ||
        orderDetails.orderStatus === OrderStatus.DRIVER_DROPPED_OFF_ORDER ||
        orderDetails.orderStatus === OrderStatus.ORDER_COMPLETED) {
      return null;
    }

    const driverLocation = driverPath[driverPath.length - 1];
    const distance = calculateDistance(
      driverLocation.latitude,
      driverLocation.longitude,
      orderDetails.assignedRestaurantBranch.latitude,
      orderDetails.assignedRestaurantBranch.longitude
    );

    return formatDistance(distance);
  };

  // Calculate distance between driver and client (when picked up)
  const getDriverToClientDistance = (): string | null => {
    if (!orderDetails?.assignedDriver || driverPath.length === 0) {
      return null;
    }

    // Only show this distance if order has been picked up
    if (orderDetails.orderStatus !== OrderStatus.DRIVER_PICKED_UP_ORDER) {
      return null;
    }

    const driverLocation = driverPath[driverPath.length - 1];
    const distance = calculateDistance(
      driverLocation.latitude,
      driverLocation.longitude,
      orderDetails.clientLatitude,
      orderDetails.clientLongitude
    );

    return formatDistance(distance);
  };

  // Action handlers
  const handleAcceptOrder = async (branchId: number) => {
    if (!orderDetails) return;

    setActionLoading(true);
    try {
      const response = await ordersService.acceptOrder({
        orderId: orderDetails.id,
        assignedBranchId: branchId
      });

      if (response.success) {
        await fetchOrderDetails(orderDetails.id);
        setAcceptModalOpen(false);
      } else {
        setError(response.error || t('orderDetails.failedToAccept'));
      }
    } catch (error) {
      setError(t('orderDetails.networkError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelOrder = async (reason: string) => {
    if (!orderDetails) return;

    setActionLoading(true);
    try {
      const response = await ordersService.cancelOrder({
        orderId: orderDetails.id,
        reason
      });

      if (response.success) {
        await fetchOrderDetails(orderDetails.id);
        setCancelModalOpen(false);
      } else {
        setError(response.error || t('orderDetails.failedToCancel'));
      }
    } catch (error) {
      setError(t('orderDetails.networkError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeBranch = async (branchId: number, reason: string) => {
    if (!orderDetails) return;

    setActionLoading(true);
    try {
      const response = await ordersService.changeBranch({
        orderId: orderDetails.id,
        branchId,
        reason
      });

      if (response.success) {
        await fetchOrderDetails(orderDetails.id);
        setChangeBranchModalOpen(false);
      } else {
        setError(response.error || t('orderDetails.failedToChangeBranch'));
      }
    } catch (error) {
      setError(t('orderDetails.networkError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDetachDriver = async (reason: string) => {
    if (!orderDetails) return;

    setActionLoading(true);
    try {
      const response = await ordersService.detachDriver({
        orderId: orderDetails.id,
        reason
      });

      if (response.success) {
        await fetchOrderDetails(orderDetails.id);
        setDetachDriverModalOpen(false);
      } else {
        setError(response.error || t('orderDetails.failedToDetachDriver'));
      }
    } catch (error) {
      setError(t('orderDetails.networkError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendToDriver = async (driverId: number) => {
    if (!orderDetails) return;

    setActionLoading(true);
    try {
      const response = await ordersService.sendOrderToDriver({
        orderId: orderDetails.id,
        driverId
      });

      if (response.success) {
        // Refetch order details to ensure we have the latest data including assigned driver
        await fetchOrderDetails(orderDetails.id);
        setSendToDriverModalOpen(false);
      } else {
        setError(response.error || t('orderDetails.failedToSendToDriver'));
      }
    } catch (error) {
      setError(t('orderDetails.networkError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendMessage = async (message: string) => {
    if (!orderDetails) return;

    setActionLoading(true);
    try {
      const response = await ordersService.sendMessageToClient({
        clientId: orderDetails.client.id,
        message
      });

      if (response.success) {
        setSendMessageModalOpen(false);
        // Show success message
        alert(t('orderDetails.messageSentSuccess'));
      } else {
        setError(response.error || t('orderDetails.failedToSendMessage'));
      }
    } catch (error) {
      setError(t('orderDetails.networkError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateDeliveryPrice = async (deliveryPrice: number) => {
    if (!orderDetails) return;

    setActionLoading(true);
    try {
      const response = await ordersService.updateDeliveryPrice({
        orderId: orderDetails.id,
        deliveryPrice
      });

      if (response.success) {
        await fetchOrderDetails(orderDetails.id);
        setUpdateDeliveryPriceModalOpen(false);
      } else {
        setError(response.error || t('orderDetails.failedToUpdateDeliveryPrice'));
      }
    } catch (error) {
      setError(t('orderDetails.networkError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendToTelegramGroup = async () => {
    if (!orderDetails) return;

    setActionLoading(true);
    try {
      const response = await ordersService.sendToTelegramGroup(orderDetails.id);

      if (response.success) {
        alert(t('orderDetails.sentToTelegramGroupSuccess'));
      } else {
        setError(response.error || t('orderDetails.failedToSendToTelegramGroup'));
      }
    } catch (error) {
      setError(t('orderDetails.networkError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleProblematicComplete = async (reason: string) => {
    if (!orderDetails) return;

    setActionLoading(true);
    try {
      const response = await ordersService.problematicCompleteOrder({
        orderId: orderDetails.id,
        reason
      });

      if (response.success) {
        await fetchOrderDetails(orderDetails.id);
        setProblematicCompleteModalOpen(false);
      } else {
        setError(response.error || t('orderDetails.failedToProblematicComplete'));
      }
    } catch (error) {
      setError(t('orderDetails.networkError'));
    } finally {
      setActionLoading(false);
    }
  };

  const canAcceptOrder = (status: OrderStatus): boolean => {
    return status === OrderStatus.ORDER_PLACED;
  };

  const canCancelOrder = (status: OrderStatus): boolean => {
    return status !== OrderStatus.ORDER_COMPLETED && status !== OrderStatus.ADMIN_CANCELLED_ORDER;
  };

  const canChangeBranch = (status: OrderStatus): boolean => {
    // Only allow changing branch after admin accepts but before restaurant accepts
    return status === OrderStatus.ORDER_ACCEPTED_BY_ADMIN;
  };

  const canDetachDriver = (order: AdminOrderDetailsResponse): boolean => {
    return !!order.assignedDriver;
  };

  const canSendToDriver = (order: AdminOrderDetailsResponse): boolean => {
    // Only allow sending to driver after restaurant accepts the order and no driver is assigned yet
    return !order.assignedDriver && (
      order.orderStatus === OrderStatus.RESTAURANT_ACCEPTED_ORDER ||
      order.orderStatus === OrderStatus.RESTAURANT_FINISHED_ORDER
    );
  };

  const canProblematicComplete = (status: OrderStatus): boolean => {
    return status !== OrderStatus.ORDER_COMPLETED && status !== OrderStatus.ADMIN_CANCELLED_ORDER;
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-content">
          <div className="loading-container">{t('orderDetails.loadingOrderDetails')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-content">
          <div className="error-message">{error}</div>
          <button onClick={() => navigate('/orders')} className="back-button">
            {t('orderDetails.backToOrders')}
          </button>
        </div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="page">
        <div className="page-content">
          <div className="no-data">{t('orderDetails.orderNotFound')}</div>
          <button onClick={() => navigate('/orders')} className="back-button">
            {t('orderDetails.backToOrders')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="header-with-back">
          <button onClick={() => navigate('/orders')} className="back-button">
            ← {t('orderDetails.backToOrders')}
          </button>
          <div>
            <h1>{t('orderDetails.title', { orderId: orderDetails.id })}</h1>
            <p>{t('orderDetails.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="page-content">
        {/* Order Status and Quick Info */}
        <div className="order-details-grid">
          <div className="detail-card">
            <h3>{t('orderDetails.orderStatus')}</h3>
            <div className="status-section">
              <div>
                <span
                  className="status-badge large"
                  style={{ backgroundColor: getStatusColor(orderDetails.orderStatus) }}
                >
                  {getStatusDisplay(orderDetails.orderStatus)}
                </span>
                {(() => {
                  if (orderDetails.orderStatus == OrderStatus.ORDER_ACCEPTED_BY_ADMIN && orderDetails.restaurantRejectionReason) {
                    return (
                      <div className="sub-text" style={{ marginTop: '8px', fontSize: '0.9rem', color: '#dc3545' }}>
                        {orderDetails.restaurantRejectionReason}
                      </div>
                    );
                  }
                  return null;
                })()}
                {orderDetails.problematicCompleteReason && (
                  <div className="sub-text" style={{ marginTop: '8px', fontSize: '0.9rem', color: '#dc3545', fontWeight: 'bold' }}>
                    {t('orderDetails.problematicCompleteReason')}: {orderDetails.problematicCompleteReason}
                  </div>
                )}
              </div>
              <div className="order-meta">
                <div>{t('orderDetails.created')}: {formatDate(orderDetails.createdAt)}</div>
                <div>{t('common.total')}: {formatPrice(calculateTotalPrice(orderDetails))}</div>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="detail-card">
            <h3>
              {t('orderDetails.customer')}
              <button
                onClick={() => window.open(`/clients/${orderDetails.client!.id}`, '_blank')}
                style={{
                  marginLeft: '1rem',
                  padding: '0.25rem 0.75rem',
                  fontSize: '0.85rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                title={t('common.viewDetails')}
              >
                {t('common.viewDetails')}
              </button>
            </h3>
            <div className="info-grid">
              <div>
                <strong>{t('common.name')}:</strong> {orderDetails.client.name || 'N/A'}
              </div>
              <div>
                <strong>{t('common.phone')}:</strong> {orderDetails.client.phone}
              </div>
              <div>
                <strong>{t('orderDetails.contactPhone')}:</strong> {orderDetails.contactPhone}
              </div>
            </div>
          </div>

          {/* Restaurant Information */}
          <div className="detail-card">
            <h3>
              {t('orderDetails.restaurant')}
              <button
                onClick={() => window.open(`/restaurants/${orderDetails.restaurant!.id}`, '_blank')}
                style={{
                  marginLeft: '1rem',
                  padding: '0.25rem 0.75rem',
                  fontSize: '0.85rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                title={t('common.viewDetails')}
              >
                {t('common.viewDetails')}
              </button>
            </h3>
            <div className="info-grid">
              <div>
                <strong>{t('common.name')}:</strong> {orderDetails.restaurant.name}
              </div>
              {orderDetails.assignedRestaurantBranch && (
                <div>
                  <strong>{t('orderDetails.branch')}:</strong> {orderDetails.assignedRestaurantBranch.name}
                </div>
              )}
              <div>
                <strong>{t('common.phone')}:</strong> {orderDetails.assignedRestaurantBranch?.phone || orderDetails.restaurant.phone}
              </div>
            </div>
          </div>

          {/* Driver Information */}
          <div className="detail-card">
            <h3>
              {t('orderDetails.driver')}
              {orderDetails.assignedDriver && (
                <button
                  onClick={() => window.open(`/drivers/${orderDetails.assignedDriver!.id}`, '_blank')}
                  style={{
                    marginLeft: '1rem',
                    padding: '0.25rem 0.75rem',
                    fontSize: '0.85rem',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  title={t('common.viewDetails')}
                >
                  {t('common.viewDetails')}
                </button>
              )}
            </h3>
            {orderDetails.assignedDriver ? (
              <div className="info-grid">
                <div>
                  <strong>{t('common.name')}:</strong> {orderDetails.assignedDriver.fullName}
                </div>
                <div>
                  <strong>{t('common.phone')}:</strong> {orderDetails.assignedDriver.phone}
                </div>
              </div>
            ) : (
              <div className="no-driver">{t('orderDetails.noDriverAssigned')}</div>
            )}
          </div>

          {/* Driver Confirmation Code */}
          <div className="detail-card">
            <h3>{t('orderDetails.confirmationCode')}</h3>
            <div className="info-grid">
              <div>
                <strong>{t('orderDetails.confirmationCodeEnabled')}:</strong>{' '}
                <span style={{
                  color: orderDetails.shouldDriverConfirmOrderCode ? '#28a745' : '#dc3545',
                  fontWeight: 'bold'
                }}>
                  {orderDetails.shouldDriverConfirmOrderCode ? t('common.yes') : t('common.no')}
                </span>
              </div>
              <div style={{
                padding: '1rem',
                backgroundColor: orderDetails.shouldDriverConfirmOrderCode ? '#f8f9fa' : '#f5f5f5',
                borderRadius: '8px',
                border: orderDetails.shouldDriverConfirmOrderCode ? '2px solid #007bff' : '2px solid #dee2e6',
                textAlign: 'center',
                opacity: orderDetails.shouldDriverConfirmOrderCode ? 1 : 0.6
              }}>
                <div style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                  {orderDetails.shouldDriverConfirmOrderCode
                    ? t('orderDetails.confirmationCodeDescription')
                    : t('orderDetails.confirmationCodeDisabled')
                  }
                </div>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: orderDetails.shouldDriverConfirmOrderCode ? '#007bff' : '#6c757d',
                  letterSpacing: '0.5rem',
                  fontFamily: 'monospace'
                }}>
                  {orderDetails.orderDriverConfirmationCode}
                </div>
              </div>
            </div>
          </div>

          {/* Client Cancel Permission */}
          <div className="detail-card">
            <h3>{t('orderDetails.clientCancelPermission')}</h3>
            <div className="info-grid">
              {(() => {
                const isCancelableStatus =
                  orderDetails.orderStatus === OrderStatus.ORDER_PLACED ||
                  orderDetails.orderStatus === OrderStatus.ORDER_ACCEPTED_BY_ADMIN;
                const canClientCancel = orderDetails.canClientCancelOrder && isCancelableStatus;

                let tooltipMessage = '';
                if (orderDetails.canClientCancelOrder && isCancelableStatus) {
                  tooltipMessage = t('orderDetails.clientCancelButtonVisibilityActive');
                } else if (orderDetails.canClientCancelOrder && !isCancelableStatus) {
                  tooltipMessage = t('orderDetails.clientCancelButtonVisibilityStatusMismatch');
                } else {
                  tooltipMessage = t('orderDetails.clientCancelButtonVisibilityDisabled');
                }

                return (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: canClientCancel ? '#d4edda' : '#f8d7da',
                    borderRadius: '8px',
                    border: canClientCancel ? '2px solid #28a745' : '2px solid #dc3545',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <div style={{
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      color: canClientCancel ? '#28a745' : '#dc3545',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span>{canClientCancel ? t('common.yes') : t('common.no')}</span>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: canClientCancel ? '#28a745' : '#6c757d',
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          cursor: 'help',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          const tooltip = e.currentTarget.querySelector('.info-tooltip') as HTMLElement;
                          if (tooltip) tooltip.style.display = 'block';
                        }}
                        onMouseLeave={(e) => {
                          const tooltip = e.currentTarget.querySelector('.info-tooltip') as HTMLElement;
                          if (tooltip) tooltip.style.display = 'none';
                        }}
                      >
                        ?
                        <span
                          className="info-tooltip"
                          style={{
                            display: 'none',
                            position: 'absolute',
                            bottom: 'calc(100% + 10px)',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#333',
                            color: '#fff',
                            padding: '0.75rem 1rem',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            zIndex: 1000,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                            pointerEvents: 'none',
                            minWidth: '300px',
                            maxWidth: '400px',
                            whiteSpace: 'normal',
                            textAlign: 'left',
                            lineHeight: '1.5'
                          }}
                        >
                          {tooltipMessage}
                          <span
                            style={{
                              content: '""',
                              position: 'absolute',
                              top: '100%',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              borderWidth: '8px',
                              borderStyle: 'solid',
                              borderColor: '#333 transparent transparent transparent'
                            }}
                          />
                        </span>
                      </span>
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                      {canClientCancel
                        ? t('orderDetails.clientCanCancelOrderNow')
                        : t('orderDetails.clientCannotCancelOrderNow')
                      }
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Distance Information */}
          {(getClientToRestaurantDistance() || getClientToAllBranchesDistances() || getDriverToRestaurantDistance() || getDriverToClientDistance()) && (
            <div className="detail-card">
              <h3>{t('orderDetails.distanceInformation')}</h3>
              <div className="info-grid">
                {/* Distance to assigned branch */}
                {getClientToRestaurantDistance() && (
                  <div>
                    <strong>{t('orderDetails.distanceClientToRestaurant')}:</strong> <span style={{ color: '#007bff' }}>{getClientToRestaurantDistance()}</span>
                  </div>
                )}

                {/* Distances to all available branches (when no branch assigned) */}
                {getClientToAllBranchesDistances() && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <strong>{t('orderDetails.distanceClientToRestaurant')} ({t('orderDetails.distanceSortedByDistance')}):</strong>
                    <div style={{ marginTop: '0.5rem' }}>
                      {getClientToAllBranchesDistances()!.map((item) => (
                        <div
                          key={item.branch.id}
                          style={{
                            padding: '0.5rem',
                            marginBottom: '0.5rem',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            border: '1px solid #dee2e6',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div>
                            <strong>{item.branch.name}</strong>
                            <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                              {item.branch.representativeName}
                            </div>
                          </div>
                          <div style={{
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            color: '#007bff'
                          }}>
                            {item.formattedDistance}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {getDriverToRestaurantDistance() && (
                  <div>
                    <strong>{t('orderDetails.distanceDriverToRestaurant')}:</strong> <span style={{ color: '#28a745' }}>{getDriverToRestaurantDistance()}</span>
                  </div>
                )}
                {getDriverToClientDistance() && (
                  <div>
                    <strong>{t('orderDetails.distanceDriverToClient')}:</strong> <span style={{ color: '#f57c00' }}>{getDriverToClientDistance()}</span>
                  </div>
                )}
                <div style={{
                  fontSize: '0.85rem',
                  color: '#6c757d',
                  fontStyle: 'italic',
                  marginTop: '0.5rem',
                  gridColumn: '1 / -1'
                }}>
                  ℹ️ {t('orderDetails.distanceNote')}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Admin Actions */}
        <div className="action-buttons">
          <h3>{t('orderDetails.actions')}</h3>
          <div className="action-buttons-grid">
            {canAcceptOrder(orderDetails.orderStatus) && (
              <button
                onClick={() => setAcceptModalOpen(true)}
                className="action-button accept"
                disabled={actionLoading}
              >
                {t('orderDetails.acceptOrder')}
              </button>
            )}

            {canCancelOrder(orderDetails.orderStatus) && (
              <button
                onClick={() => setCancelModalOpen(true)}
                className="action-button cancel"
                disabled={actionLoading}
              >
                {t('orderDetails.cancelOrder')}
              </button>
            )}

            {canChangeBranch(orderDetails.orderStatus) && orderDetails.allRestaurantBranches.length > 1 && (
              <button
                onClick={() => setChangeBranchModalOpen(true)}
                className="action-button change-branch"
                disabled={actionLoading}
              >
                {t('orderDetails.changeBranch')}
              </button>
            )}

            {canDetachDriver(orderDetails) && (
              <button
                onClick={() => setDetachDriverModalOpen(true)}
                className="action-button detach-driver"
                disabled={actionLoading}
              >
                {t('orderDetails.detachDriver')}
              </button>
            )}

            {canSendToDriver(orderDetails) && (
              <button
                onClick={() => setSendToDriverModalOpen(true)}
                className="action-button send-to-driver"
                disabled={actionLoading}
              >
                {t('orderDetails.sendToDriver')}
              </button>
            )}

            <button
              onClick={() => setSendMessageModalOpen(true)}
              className="action-button send-message"
              disabled={actionLoading}
            >
              {t('orderDetails.messageClient')}
            </button>

            {canCancelOrder(orderDetails.orderStatus) && (
              <button
                onClick={() => setUpdateDeliveryPriceModalOpen(true)}
                className="action-button update-price"
                disabled={actionLoading}
                >
                {t('orderDetails.updateDeliveryPrice')}
              </button>
            )}

            {canCancelOrder(orderDetails.orderStatus) && (
              <button
                onClick={handleSendToTelegramGroup}
                className="action-button send-telegram"
                disabled={actionLoading}
              >
                {t('orderDetails.sendToTelegramGroup')}
              </button>
            )}

            {canProblematicComplete(orderDetails.orderStatus) && (
              <button
                onClick={() => setProblematicCompleteModalOpen(true)}
                className="action-button"
                disabled={actionLoading}
                style={{ backgroundColor: '#ff6b35', color: 'white' }}
              >
                {t('orderDetails.problematicComplete')}
              </button>
            )}
          </div>
        </div>

        {/* Map */}
        <div className="detail-card full-width">
          <div className="detail-card-header">
            <h3>{t('orderDetails.orderMap')}</h3>
            <button
              className="map-refresh-button"
              onClick={handleRefreshDriverPath}
              disabled={!orderDetails.assignedDriver || driverPathRefreshing}
              title={t('orderDetails.refreshDriverPath')}
            >
              {driverPathRefreshing ? t('orderDetails.refreshingDriverPath') : t('orderDetails.refreshDriverPath')}
            </button>
          </div>
          <OrderMap orderDetails={orderDetails} nearbyDrivers={nearbyDrivers} driverPath={driverPath} />
        </div>

        {/* Status History */}
        <div className="detail-card full-width">
          <h3>{t('orderDetails.statusHistory')}</h3>
          <div className="status-history">
            {orderDetails.statusChanges.map((change) => {
              const isRejection = change.orderStatusChange === OrderStatusChange.RESTAURANT_REJECTED_ORDER;
              return (
                <div
                  key={change.id}
                  className={`status-change-item ${isRejection ? 'rejection' : ''}`}
                  style={isRejection ? { borderLeft: '4px solid #dc3545', backgroundColor: '#fff5f5' } : {}}
                >
                  <div className="status-change-header">
                    <span className="status-change-title" style={isRejection ? { color: '#dc3545', fontWeight: 'bold' } : {}}>
                      {getStatusChangeDisplay(change.orderStatusChange)}
                    </span>
                    <span className="status-change-date">
                      {formatDate(change.datetime)}
                    </span>
                  </div>
                  {change.message && (
                    <div className="status-change-message">{change.message}</div>
                  )}
                  {change.reason && (
                    <div className="status-change-reason" style={isRejection ? { color: '#dc3545' } : {}}>
                      {t('orderDetails.reason')}: {change.reason}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Delivery Details */}
        <div className="detail-card full-width">
          <h3>{t('orderDetails.deliveryDetails')}</h3>
          <div className="info-grid">
            <div>
              <strong>{t('orderDetails.location')}:</strong> {orderDetails.clientLocationName}
            </div>
            <div>
              <strong>{t('orderDetails.coordinates')}:</strong> {orderDetails.clientLatitude.toFixed(6)}, {orderDetails.clientLongitude.toFixed(6)}
            </div>
            {orderDetails.clientExtraNotesForDriver && (
              <div>
                <strong>{t('orderDetails.notesForDriver')}:</strong> {orderDetails.clientExtraNotesForDriver}
              </div>
            )}
            {orderDetails.clientExtraNotesForRestaurant && (
              <div>
                <strong>{t('orderDetails.notesForRestaurant')}:</strong> {orderDetails.clientExtraNotesForRestaurant}
              </div>
            )}
            {orderDetails.assignedRestaurantBranch && (
              <div>
                <strong>{t('orderDetails.restaurantRepresentative')}:</strong> {orderDetails.assignedRestaurantBranch.representativeName}
              </div>
            )}
          </div>
        </div>

        {/* Ordered Foods */}
        <div className="detail-card full-width">
          <h3>{t('orderDetails.orderedItems')}</h3>
          <div className="foods-list">
            {orderDetails.orderedFoods.map((food) => (
              <div key={food.id} className="food-item">
                <div className="food-info">
                  <div className="food-name">{food.name}</div>
                  <div className="food-details">
                    {formatPrice(food.price)} × {food.count} = {formatPrice(food.price * food.count)}
                  </div>
                </div>
              </div>
            ))}
            <div className="price-summary">
              <div className="price-row">
                <span>{t('orderDetails.foodTotal')}:</span>
                <span>{formatPrice(orderDetails.orderedFoods.reduce((sum, food) => sum + (food.price * food.count), 0))}</span>
              </div>
              <div className="price-row">
                <span>{t('orderDetails.delivery')}:</span>
                <span>{formatPrice(orderDetails.deliveryPrice)}</span>
              </div>
              <div className="price-row total">
                <span>{t('common.total')}:</span>
                <span>{formatPrice(calculateTotalPrice(orderDetails))}</span>
              </div>

              {/* Platform Profits Section */}
              <div style={{
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '2px solid #dee2e6'
              }}>
                <div className="price-row" style={{
                  color: '#28a745',
                  fontWeight: 'bold',
                  fontSize: '1.05rem'
                }}>
                  <span>{t('orderDetails.platformProfits')}:</span>
                  <span></span>
                </div>

                {/* Platform profit from delivery */}
                <div className="price-row" style={{
                  paddingLeft: '1rem',
                  fontSize: '0.95rem'
                }}>
                  <span>
                    {t('orderDetails.fromDelivery')}
                    <span style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                      {' '}({orderDetails.driverPricePercentageForPlatform}%)
                    </span>:
                  </span>
                  <span style={{ color: '#28a745', fontWeight: '600' }}>
                    {formatPrice(Math.round(orderDetails.deliveryPrice * orderDetails.driverPricePercentageForPlatform / 100))}
                  </span>
                </div>

                {/* Platform profit from food */}
                <div className="price-row" style={{
                  paddingLeft: '1rem',
                  fontSize: '0.95rem'
                }}>
                  <span>
                    {t('orderDetails.fromFood')}
                    <span style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                      {' '}({orderDetails.restaurantFoodPricePercentageForPlatform}%)
                    </span>:
                  </span>
                  <span style={{ color: '#28a745', fontWeight: '600' }}>
                    {formatPrice(Math.round(orderDetails.orderedFoods.reduce((sum, food) => sum + (food.price * food.count), 0) * orderDetails.restaurantFoodPricePercentageForPlatform / 100))}
                  </span>
                </div>

                {/* Total platform profit */}
                <div className="price-row" style={{
                  marginTop: '0.5rem',
                  paddingTop: '0.5rem',
                  borderTop: '1px solid #dee2e6',
                  color: '#28a745',
                  fontWeight: 'bold',
                  fontSize: '1.05rem'
                }}>
                  <span>{t('orderDetails.totalPlatformProfit')}:</span>
                  <span>
                    {formatPrice(
                      Math.round(orderDetails.deliveryPrice * orderDetails.driverPricePercentageForPlatform / 100) +
                      Math.round(orderDetails.orderedFoods.reduce((sum, food) => sum + (food.price * food.count), 0) * orderDetails.restaurantFoodPricePercentageForPlatform / 100)
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Order Rating */}
        {(orderDetails.orderStatus === OrderStatus.ORDER_COMPLETED ||
          orderDetails.orderStatus === OrderStatus.ADMIN_CANCELLED_ORDER) && (
          <div className="detail-card full-width">
            <h3>{t('reviews.orderRating')}</h3>
            {ratingLoading ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
                {t('common.loading')}
              </div>
            ) : orderRating ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Restaurant Rating */}
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <strong style={{ fontSize: '1rem' }}>{t('reviews.restaurantRating')}</strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {renderStars(orderRating.restaurantRating)}
                      <span style={{ fontSize: '1rem', fontWeight: 'bold', marginLeft: '4px' }}>
                        {orderRating.restaurantRating}/5
                      </span>
                    </div>
                  </div>
                  {orderRating.restaurantNote && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      fontStyle: 'italic',
                      color: '#495057'
                    }}>
                      "{orderRating.restaurantNote}"
                    </div>
                  )}
                </div>

                {/* Driver Rating */}
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <strong style={{ fontSize: '1rem' }}>{t('reviews.driverRating')}</strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {renderStars(orderRating.driverRating)}
                      <span style={{ fontSize: '1rem', fontWeight: 'bold', marginLeft: '4px' }}>
                        {orderRating.driverRating}/5
                      </span>
                    </div>
                  </div>
                  {orderRating.driverNote && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      fontStyle: 'italic',
                      color: '#495057'
                    }}>
                      "{orderRating.driverNote}"
                    </div>
                  )}
                </div>

                {/* Service Rating */}
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <strong style={{ fontSize: '1rem' }}>{t('reviews.serviceRating')}</strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {renderStars(orderRating.serviceRating)}
                      <span style={{ fontSize: '1rem', fontWeight: 'bold', marginLeft: '4px' }}>
                        {orderRating.serviceRating}/5
                      </span>
                    </div>
                  </div>
                  {orderRating.serviceNote && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      fontStyle: 'italic',
                      color: '#495057'
                    }}>
                      "{orderRating.serviceNote}"
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                color: '#666',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
              }}>
                {t('reviews.orderNotRated')}
              </div>
            )}
          </div>
        )}

        {/* Order Metadata */}
        {metadata.length > 0 && (
          <div className="detail-card full-width">
            <h3>Metadata</h3>
            <div className="info-grid">
              {metadata.map((item) => (
                <div key={item.id}>
                  <strong>{item.key}:</strong> {item.value}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <AcceptOrderModal
        isOpen={acceptModalOpen}
        onClose={() => setAcceptModalOpen(false)}
        onAccept={handleAcceptOrder}
        branches={orderDetails.allRestaurantBranches}
        loading={actionLoading}
      />

      <CancelOrderModal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onCancel={handleCancelOrder}
        loading={actionLoading}
      />

      <ChangeBranchModal
        isOpen={changeBranchModalOpen}
        onClose={() => setChangeBranchModalOpen(false)}
        onChange={handleChangeBranch}
        branches={orderDetails.allRestaurantBranches}
        currentBranchId={orderDetails.assignedRestaurantBranch?.id}
        loading={actionLoading}
      />

      <DetachDriverModal
        isOpen={detachDriverModalOpen}
        onClose={() => setDetachDriverModalOpen(false)}
        onDetach={handleDetachDriver}
        driverName={orderDetails.assignedDriver?.fullName}
        loading={actionLoading}
      />

      <SendToDriverModal
        isOpen={sendToDriverModalOpen}
        onClose={() => setSendToDriverModalOpen(false)}
        onSend={handleSendToDriver}
        loading={actionLoading}
      />

      <SendMessageModal
        isOpen={sendMessageModalOpen}
        onClose={() => setSendMessageModalOpen(false)}
        onSend={handleSendMessage}
        clientName={orderDetails.client.name || 'Client'}
        loading={actionLoading}
      />

      <UpdateDeliveryPriceModal
        isOpen={updateDeliveryPriceModalOpen}
        onClose={() => setUpdateDeliveryPriceModalOpen(false)}
        onUpdate={handleUpdateDeliveryPrice}
        currentPrice={orderDetails.deliveryPrice}
        loading={actionLoading}
      />

      <ProblematicCompleteModal
        isOpen={problematicCompleteModalOpen}
        onClose={() => setProblematicCompleteModalOpen(false)}
        onComplete={handleProblematicComplete}
        loading={actionLoading}
      />
    </div>
  );
};

export default OrderDetails;